<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { EquipInfo } from '~/models/dto-types';
import { useEquipStore } from '~/store/equipStore';
import { useTocStore } from '~/store/tocStore';
// Images
// import InverterPvIcon from "@/assets/images/energy_icon1.svg";  // 태양광 발전
// import InverterSolarheatIcon from "@/assets/images/energy_icon2.svg"; // 태양열 발전
// import InverterFuelcellIcon from "@/assets/images/energy_icon3.svg"; // 연료전지 발전
// import InverterGeoIcon from "@/assets/images/energy_icon4.svg"; // 지열 발전
// import InverterWfIcon from "@/assets/images/energy_icon5.svg"; // 풍력 발전
// import InverterEssIcon from "@/assets/images/energy_icon6.svg"; // ESS 발전

import InverterPvIcon from "@/assets/images/renewal/energy_icon1.svg";  // 태양광 발전
import InverterSolarheatIcon from "@/assets/images/renewal/energy_icon2.svg"; // 태양열 발전
import InverterFuelcellIcon from "@/assets/images/new_energy_icon3.svg"; // 연료전지 발전
import InverterGeoIcon from "@/assets/images/renewal/energy_icon4.svg"; // 지열 발전
import InverterWfIcon from "@/assets/images/new_energy_icon5.svg"; // 풍력 발전
import InverterEssIcon from "@/assets/images/new_energy_icon6.svg"; // ESS 발전
import InverterMultiIcon from "@/assets/images/new_energy_icon7.svg"; // 복수 에너지원


const tocStore = useTocStore();
const equipStore = useEquipStore();
const route = useRoute();
const router = useRouter();

const props = defineProps<{
  apiData?: any,
  property?: any,
}>();

const emits = defineEmits(['go'])

const invertData = computed(() => props.apiData);

/** 위젯 설정 `property.count` — 페이지당 표시 슬롯 수(기본 5) */
const pageSize = computed(() => {
  const c = props.property?.count;
  return typeof c === 'number' && c > 0 ? c : 5;
});

const userEquips = computed(() => invertData.value?.userEquips ?? []);

/** 설비를 `pageSize`개씩 나눈 페이지. 마지막 페이지는 부족분을 `undefined` 슬롯으로 채움 */
const equipPages = computed(() => {
  const size = pageSize.value;
  const list = userEquips.value;
  const pages: (any | undefined)[][] = [];

  if (!list.length) {
    pages.push(Array.from({ length: size }, () => undefined));
    return pages;
  }

  for (let i = 0; i < list.length; i += size) {
    const chunk = list.slice(i, i + size);
    while (chunk.length < size) {
      chunk.push(undefined);
    }
    pages.push(chunk);
  }
  return pages;
});

const isSwipeable = computed(() => equipPages.value.length > 1);

const scrollEl = ref<HTMLElement | null>(null);
const AUTO_SCROLL_INTERVAL_MS = 3000;
let autoScrollTimer: ReturnType<typeof setInterval> | null = null;

function clearAutoScroll() {
  if (autoScrollTimer !== null) {
    clearInterval(autoScrollTimer);
    autoScrollTimer = null;
  }
}

function tickAutoScroll() {
  const el = scrollEl.value;
  if (!el || !isSwipeable.value) {
    return;
  }
  const step = el.clientHeight;
  if (step <= 0) {
    return;
  }
  const maxTop = Math.max(0, el.scrollHeight - step);
  if (el.scrollTop >= maxTop - 2) {
    el.scrollTo({ top: 0, behavior: 'smooth' });
  } else {
    el.scrollTo({ top: el.scrollTop + step, behavior: 'smooth' });
  }
}

function startAutoScroll() {
  clearAutoScroll();
  if (!isSwipeable.value || !scrollEl.value) {
    return;
  }
  autoScrollTimer = setInterval(tickAutoScroll, AUTO_SCROLL_INTERVAL_MS);
}

watch(
  [isSwipeable, () => equipPages.value.length],
  () => {
    nextTick(() => {
      startAutoScroll();
    });
  },
  { flush: 'post' },
);

function onCardClick(equip: any) {
  if (equip === undefined || equip === null) {
    return;
  }
  selectEquip(equip);
}

async function selectEquip(equipInfo: any){

  // // equipStore.getSiteEquips()는 내부에서 equipStore.selectEquipInfo를 업데이트하므로
  // // 반환된 데이터를 사용하여 tocStore에만 설정
  const equipData = await equipStore.getSiteEquips('equip', equipInfo.cid);
  if (equipData) {
    tocStore.setSelectEquipInfo(equipData as EquipInfo);
  } else {
    // API 호출 실패 시 전달받은 equipInfo 사용
    tocStore.setSelectEquipInfo(equipInfo as EquipInfo);
  }

  router.push({name: 'sites-detail-default-equip-equipid', params: {equipid: equipInfo.cid}});
}

onMounted(() => {
  console.log("invertData.value :: ", invertData.value);
  nextTick(() => {
    startAutoScroll();
  });
});

onUnmounted(() => {
  clearAutoScroll();
});

</script>

<template>

 <div
    class="invert-card-list h-100"
    :class="invertData !== null ? 'invert-card-list--paged' : 'pl-1'"
  ><!-- 데이터 있음: 좌우 대칭 패딩(--paged). 없음: 기존 pl-1 -->

    <template v-if="invertData !== null">
      <div
        ref="scrollEl"
        class="invert-card-list__scroll"
        :class="{ 'invert-card-list__scroll--single': !isSwipeable }"
      >
        <div
          v-for="(pageSlots, pageIdx) in equipPages"
          :key="pageIdx"
          class="invert-card-list__page"
        >
          <div
            v-for="(equip, slotIdx) in pageSlots"
            :key="`${pageIdx}-${slotIdx}`"
            class="invert-card common-card"
            :class="{ 'empty-card': equip === undefined }"
            @click="onCardClick(equip)"
          >
            <template v-if="equip !== undefined">
              <div class="icon">
                <div class="image-container">
                  <!-- 태양광 발전 -->
                  <img :src="InverterPvIcon" alt="invert-card" v-if="equip.engSource === '10'" />
                  <!-- 태양열 발전 -->
                  <img :src="InverterSolarheatIcon" alt="invert-card" v-else-if="equip.engSource === '20'" />
                  <!-- 연료전지 발전 -->
                  <img :src="InverterFuelcellIcon" alt="invert-card" v-else-if="equip.engSource === '60'" />
                  <!-- 지열 발전 -->
                  <img :src="InverterGeoIcon" alt="invert-card" v-else-if="equip.engSource === '30'" />
                  <!-- 풍력 발전 -->
                  <img :src="InverterWfIcon" alt="invert-card" v-else-if="equip.engSource === '40'" />
                  <!-- ESS 발전 -->
                  <img :src="InverterEssIcon" alt="invert-card" v-else-if="equip.engSource === '70'" />
                  <!-- 복수 에너지원 -->
                  <img :src="InverterMultiIcon" alt="invert-card" v-else />
                </div>

                <!-- <button 
                  class="common-btn"
                  type="button"
                  
                >
                  이동하기
                </button> -->
              </div>

              <div class="equip-info">
                <div class="name">
                  {{ commonUtils.isAllStrDataEmpty(equip.equipName) }}

                  <span
                    class="equip-status"
                    :class="equip.equipStatus"
                  >
                    {{ commonUtils.isAllStrDataEmpty(equip.equipStatusName) }}
                  </span>
                </div>
                <div class="capacity">설비용량 {{ commonUtils.isAllStrDataEmpty(equip.equipCapacity) }}kWp</div>
              </div>

              <div class="equip-wrapper">
                <div class="equip-prod">
                  <div class="today">금일 발전량</div>
                  <div class="prod">
                    <!-- 0값을 제외하고 무조건 소수점 첫번째 자리까지 출력 (ex: 0.0) -->
                    {{ commonUtils.isAllStrDataEmpty(equip.sumProdKWH) }}
                    <span class="unit">kWh</span>
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="icon">
                <div class="image-container"></div>
              </div>
              <div class="equip-info"></div>
              <div class="equip-wrapper">
                <div class="equip-prod"></div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>
    <div 
      v-else
      class="invert-card common-card empty-card" 
      v-for="(count, i) in property.count"
    >
     <div class="icon">
        <div class="image-container">
         
        </div>
      </div>

      <div class="equip-info">
  
      </div>
      
      <div class="equip-wrapper">
        <div class="equip-prod">
    
        </div>
      </div>
    </div>
  </div>

</template>

<style lang="scss" scoped>
@use "@/assets/styles/design-toc/invert-card.scss";
@use "@/assets/styles/design-toc/define.scss";
@use "@/assets/styles/design-toc/common-card.scss";
@use "@/assets/styles/design-toc/common-button.scss";

.empty-card {
  filter: brightness(0.75);
  cursor: not-allowed !important;
}

.common-card {
  padding: 4px 16px !important;
}

/* 다중 페이지: 한 뷰포트(스크롤 영역)당 pageSize개만 스냅 — 카드 간 gap(6px)만 유지, 상·하단 빈 여백 제거 */
.invert-card-list--paged {
  min-height: 0;
  /* 기존 pl-1(0.25rem)과 동일 간격을 좌우에 맞춰 스크롤 래퍼 기준으로 대칭 */
  padding-inline: 0.25rem;
  box-sizing: border-box;
  /* 루트 .invert-card-list는 invert-card.scss에서 justify-content: center — 단일 자식(스크롤)이 높이를 꽉 채우도록 상단 정렬 */
  justify-content: flex-start;
}

.invert-card-list__scroll {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  height: 100%;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  scroll-snap-type: y mandatory;
  -webkit-overflow-scrolling: touch;
}

.invert-card-list__scroll--single {
  overflow-y: hidden;
  scroll-snap-type: none;
  touch-action: auto;
}

.invert-card-list__page {
  box-sizing: border-box;
  flex: 0 0 auto;
  min-height: 100%;
  height: 100%;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  /* 스크롤 영역(overflow) 안에서 box-shadow·모서리가 잘리지 않도록 미세 inset — 카드는 100%로 그 안을 채움 */
  /* 하단 여백은 스크롤 래퍼가 아닌 페이지 안에 둠(스크롤 래퍼 padding 시 다음 스냅 페이지 상단이 비침) */
  padding: 0 3px 1px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 6px;
  scroll-snap-align: start;
  scroll-snap-stop: always;
}

/* 한 페이지 안에서 카드 높이를 균등 분배(상하 공백 없이 영역 채움), 가로는 컨테이너에 맞춰 축소 가능 */
.invert-card-list__page > .invert-card {
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  height: auto;
  margin-inline: 0;
  box-sizing: border-box;
}

.invert-card-list__page > .invert-card .equip-info {
  min-width: 0;
}

.invert-card-list__page > .invert-card .equip-wrapper {
  min-width: 0;
}
</style>